import * as React from 'react';
const { connect } = require('react-redux');
import { translate } from 'react-i18next';
import { IStore } from 'redux/IStore';
import {
	searchArticleTags,
	deleteArticleTag,
	setSearchFilter,
	setLanguageFilter,
	resetArticleTagsData,
	resetFilters,
} from 'redux/modules/magazine/article/articletags';
import { createArticleTag } from 'modules/magazine/article/managearticletag';
import { showModal, closeModal } from 'modules/modal';
import { IModalAction } from 'models/modal';
import { IArticleTagsData } from 'models/magazine/article/articletags';
import { IManageArticleTagAction, IArticleTagManageDomainValue } from 'models/magazine/article/managearticletag';
import { IArticleTagDomainValue } from 'models/magazine/article/articletagdomainvalue';
import { ILanguage } from 'models/common';
import * as c from 'models/constants';
import { IUserPreferences } from 'models/user/userpreferences';
import { ItemCardList } from 'components';
import { ErrorPage } from 'containers';
import { FilteredPage } from 'components/PortalStructure';
import { IInfiniteScrollProps } from 'models/infinitescroll';
import { IItemCardList } from 'models/itemcard';

interface IProps {
	userPreferences?: IUserPreferences;
	articleTagsData: IArticleTagsData;
	languageDomainValues: ILanguage[];
	location: any;
	router: any;
	searchArticleTags: () => IManageArticleTagAction;
	setSearchFilter?: (keywordFilter: string) => IManageArticleTagAction;
	setLanguageFilter?: (languageKeyFilter: string) => IManageArticleTagAction;
	createArticleTag: (articleTag: IArticleTagManageDomainValue) => IManageArticleTagAction;
	deleteArticleTag: (id: number) => IManageArticleTagAction;
	resetArticleTagsData: () => IManageArticleTagAction;
	resetFilters: () => IManageArticleTagAction;
	showModal: (modalType: string, modalProps: any) => IModalAction;
	closeModal: () => IModalAction;
	t?: (key: string) => string;
}

@connect(
	(state: IStore) => ({
		articleTagsData: state.articleTags,
		languageDomainValues: state.domainValues.languages,
		userPreferences: state.userPreferences,
	}),
	(dispatch) => ({
		searchArticleTags: () => dispatch(searchArticleTags()),
		createArticleTag: (tag: IArticleTagDomainValue) => dispatch(createArticleTag(tag)),
		deleteArticleTag: (id: number) => dispatch(deleteArticleTag(id)),
		setLanguageFilter: (languageKey: string) => dispatch(setLanguageFilter(languageKey)),
		setSearchFilter: (keyword: string) => dispatch(setSearchFilter(keyword)),
		resetArticleTagsData: () => dispatch(resetArticleTagsData()),
		resetFilters: () => dispatch(resetFilters()),
		closeModal: () => dispatch(closeModal()),
		showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
	}),
)
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ArticleTags extends React.Component<IProps> {
	constructor(props) {
		super(props);

		this.handleLanguageChange = this.handleLanguageChange.bind(this);
		this.handleSearchTagsValueChange = this.handleSearchTagsValueChange.bind(this);
		this.searchTags = this.searchTags.bind(this);
		this.handleSearchEnterPress = this.handleSearchEnterPress.bind(this);
		this.deleteArticleTag = this.deleteArticleTag.bind(this);
		this.loadMoreData = this.loadMoreData.bind(this);
		this.openDeleteArticleTag = this.openDeleteArticleTag.bind(this);
	}

	public componentDidMount() {
		this.props.searchArticleTags();
	}

	public loadMoreData() {
		this.props.searchArticleTags();
	}

	private openDeleteArticleTag(_, itemId: string) {
		const dialogContent = (
			<div className="confirmationDialogModal__contentContainer">
				<img src={require('containers/Html/icons/icon_notification.png')} />
				<h2>{this.props.t('BSPortal_Common_ConfirmationDialog_Sure')}</h2>
				<p>{this.props.t('Article_ArticleTags_ArticleTags_AboutDelete')}</p>
				<p>{this.props.t('BSPortal_Common_ConfirmationDialog_Confirm')}</p>
			</div>
		);

		this.props.showModal('ConfirmationDialog', {
			confirmAction: this.deleteArticleTag,
			value: itemId,
			yesButtonText: this.props.t('BSPortal_Common_YesDelete'),
			noButtonText: this.props.t('BSPortal_Common_NoWait'),
			content: dialogContent,
			closeModal: this.props.closeModal,
		});
	}

	private deleteArticleTag(confirm: boolean, key: any) {
		if (confirm) {
			this.props.deleteArticleTag(key);
		}
		this.props.closeModal();
	}

	public handleLanguageChange(value: string) {
		this.props.setLanguageFilter(value);
		this.props.searchArticleTags();
	}

	private handleSearchTagsValueChange(value: string) {
		this.props.setSearchFilter(value.length > 0 ? value : '');
	}

	private handleSearchEnterPress() {
		this.props.searchArticleTags();
	}

	private searchTags() {
		this.props.searchArticleTags();
	}

	public render() {
		if (this.props.articleTagsData.error) {
			return <ErrorPage {...this.props} message={this.props.articleTagsData.message} />;
		}
		const { keywordFilter, languageKeyFilter, itemsPage, items } = this.props.articleTagsData;
		const { t, languageDomainValues } = this.props;

		let searchFilter: string = keywordFilter;
		if (!searchFilter) {
			searchFilter = '';
		}

		let tags = [];
		if (this.props.articleTagsData !== undefined || this.props.articleTagsData !== null) {
			tags = this.props.articleTagsData.items;
		}

		const infiniteScrollProps: IInfiniteScrollProps = {
			dataLength: items.length,
			next: this.loadMoreData,
			hasMore: itemsPage.totalItems > items.length,
			loader: <h4>{t('BSPortal_Common_Loading')} </h4>,
		};

		const listProps: IItemCardList = {
			items: tags,
			itemType: c.NODE_TYPE_ARTICLE_TAG_ITEM_CARD,
			isInfiniteScroll: true,
			infiniteScrollProps,
			useIntersectionObserver: true,
			deleteItem: this.openDeleteArticleTag,
			currentUserId: this.props.userPreferences.id,
			currentUserIsAdministrator: this.props.userPreferences.isAdministrator,
			currentUserIsEditor: this.props.userPreferences.isEditor,
		};

		let languageOptions = [];
		if (languageDomainValues.length > 0) {
			languageOptions = languageDomainValues.map((language: ILanguage) => {
				return {
					key: language.key,
					value: language.value,
				};
			});
		}

		const topBarProps: object = {
			searchKeyword: keywordFilter,
			leftFilterTitle: t('BSPortal_Common_Filter_Language'),
			leftFilterKey: languageKeyFilter,
			leftFilterOptions: languageOptions,
			onSearchButtonClick: this.searchTags,
			onSearchEnterPress: this.handleSearchEnterPress,
			onSearchValueChange: this.handleSearchTagsValueChange,
			onLeftFilterChange: this.handleLanguageChange,
		};

		return (
			<FilteredPage filterBarOptions={topBarProps}>
				<ItemCardList {...listProps} />
			</FilteredPage>
		);
	}
}

export { ArticleTags };
